import { useMemo, useState } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  addMonths,
  subMonths,
  parseISO,
} from 'date-fns';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useTasksQuery } from '@/hooks/useTasks';
import { useTaskPanelStore } from '@/stores/taskPanelStore';
import { cn } from '@/lib/utils';

interface CalendarViewProps {
  sheetId: string;
  view?: 'month' | 'week';
  className?: string;
}

interface TaskEvent {
  id: string;
  name: string;
  startDate: Date;
  endDate: Date;
  percentComplete: number;
  isCritical: boolean;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function CalendarView({
  sheetId,
  view: _view = 'month',
  className,
}: CalendarViewProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const { data: tasks = [], isLoading } = useTasksQuery(sheetId);
  const { openPanel } = useTaskPanelStore();

  // Convert tasks to calendar events
  const events = useMemo(() => {
    return tasks
      .filter((task) => task.startDate && task.finishDate)
      .map((task) => ({
        id: task.id,
        name: task.name,
        startDate: parseISO(task.startDate!),
        endDate: parseISO(task.finishDate!),
        percentComplete: task.percentComplete,
        isCritical: task.isCritical,
      }));
  }, [tasks]);

  // Get days to display
  const days = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentDate]);

  // Get events for a specific day
  const getEventsForDay = (day: Date): TaskEvent[] => {
    return events.filter((event) => {
      return (
        (isSameDay(event.startDate, day) || isSameDay(event.endDate, day)) ||
        (event.startDate <= day && event.endDate >= day)
      );
    });
  };

  const handleEventClick = (taskId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    openPanel(taskId);
  };

  const goToPreviousMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex items-center gap-2 text-muted-foreground">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <span>Loading calendar...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex h-full flex-col', className)}>
      {/* Calendar Header */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold">
            {format(currentDate, 'MMMM yyyy')}
          </h2>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={goToPreviousMonth}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={goToToday}>
              Today
            </Button>
            <Button variant="ghost" size="icon" onClick={goToNextMonth}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Weekday Headers */}
      <div className="grid grid-cols-7 border-b">
        {WEEKDAYS.map((day) => (
          <div
            key={day}
            className="border-r py-2 text-center text-xs font-medium text-muted-foreground last:border-r-0"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="flex-1 grid grid-cols-7 overflow-auto">
        {days.map((day, index) => {
          const dayEvents = getEventsForDay(day);
          const isCurrentMonth = isSameMonth(day, currentDate);
          const isCurrentDay = isToday(day);

          return (
            <div
              key={day.toISOString()}
              className={cn(
                'min-h-24 border-b border-r p-1 last:border-r-0',
                !isCurrentMonth && 'bg-muted/30',
                index % 7 === 6 && 'border-r-0'
              )}
            >
              {/* Day Number */}
              <div
                className={cn(
                  'mb-1 flex h-6 w-6 items-center justify-center rounded-full text-sm',
                  isCurrentDay && 'bg-primary text-primary-foreground',
                  !isCurrentMonth && 'text-muted-foreground'
                )}
              >
                {format(day, 'd')}
              </div>

              {/* Events */}
              <div className="space-y-0.5">
                {dayEvents.slice(0, 3).map((event) => (
                  <button
                    key={event.id}
                    className={cn(
                      'w-full truncate rounded px-1 py-0.5 text-left text-xs transition-colors',
                      event.isCritical
                        ? 'bg-destructive/20 text-destructive hover:bg-destructive/30'
                        : 'bg-primary/20 text-primary hover:bg-primary/30'
                    )}
                    onClick={(e) => handleEventClick(event.id, e)}
                    title={event.name}
                  >
                    {event.name}
                  </button>
                ))}
                {dayEvents.length > 3 && (
                  <Badge variant="secondary" className="h-4 text-[10px]">
                    +{dayEvents.length - 3} more
                  </Badge>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty state */}
      {events.length === 0 && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/80">
          <CalendarIcon className="mb-4 h-12 w-12 text-muted-foreground" />
          <p className="text-lg font-medium text-muted-foreground">
            No tasks with dates
          </p>
          <p className="text-sm text-muted-foreground">
            Add start and end dates to tasks to see them here
          </p>
        </div>
      )}
    </div>
  );
}

export default CalendarView;
